import {
  Autocomplete,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Grid } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useMemo } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  setManualReceiptsPayload,
  setReceipts,
} from "../../../../../features/financeReceiptSlice";

const status = [{ label: "Final" }, { label: "Not Final" }];
const ReceiptForm = ({
  receipt,
  receiptNumber,
  canDelete,
  onDelete,
  handleFieldChange,
  companyOptions,
  currencyOptions,
  duplicateReceiptNumbers,
  totalReceipts,
}: any) => {
  let title = "";

  if (totalReceipts === 1) {
    title = "Required Fields";
  } else {
    title = `Receipt #${receiptNumber}`;
  }

  return (
    <Grid
      container
      spacing={2}
      mb={2}
      pb={3}
      sx={{ borderBottom: "1px solid #ccc" }}
    >
      <Grid size={12}>
        <Typography
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {" "}
          <span>{title}</span>
          {canDelete && (
            <IconButton onClick={() => onDelete(receipt.id)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Typography>
      </Grid>

      <Grid size={4}>
        <TextField
          label="Receipt Number"
          value={receipt.receiptNumber}
          color="secondary"
          onChange={(e) =>
            handleFieldChange(receipt.id, "receiptNumber", e.target.value)
          }
          placeholder="The transaction serial number "
          fullWidth
          sx={{ mb: 2 }}
          size="small"
          error={
            !!receipt.receiptNumber &&
            duplicateReceiptNumbers.has(receipt.receiptNumber)
          }
          helperText={
            duplicateReceiptNumbers.has(receipt.receiptNumber)
              ? "Receipt number must be unique"
              : ""
          }
        />
        <TextField
          label="Receipt Amount"
          value={receipt.receiptAmount}
          color="secondary"
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*\.?\d*$/.test(value)) {
              handleFieldChange(receipt.id, "receiptAmount", value);
            }
          }}
          placeholder="The sum listed on the receipt"
          fullWidth
          size="small"
          inputProps={{
            inputMode: "decimal",
            pattern: "[0-9.]*",
          }}
        />
      </Grid>
      <Grid size={4}>
        <Autocomplete
          sx={{ width: "100%", mb: 2 }}
          options={companyOptions || []}
          value={receipt.company}
          onChange={(_, newValue) =>
            handleFieldChange(receipt.id, "company", newValue)
          }
          isOptionEqualToValue={(option, value) =>
            option?.shared_uid === value?.shared_uid
          }
          getOptionLabel={(option) => option?.label ?? ""}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Finance Company Name"
              placeholder="Who issued this receipt"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ ml: 1 }} />
                  </InputAdornment>
                ),
              }}
              color="secondary"
              size="small"
            />
          )}
        />

        <Autocomplete
          size="small"
          options={currencyOptions || []}
          value={receipt.currency}
          isOptionEqualToValue={
            (option, value) => option?.shared_uid === value?.shared_uid // Add this
          }
          onChange={(_, newValue) =>
            handleFieldChange(receipt.id, "currency", newValue)
          }
          getOptionLabel={(option) => `${option.symbol} ${option.code}`}
          renderOption={(props, option) => (
            <li {...props}>
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: 500,
                  marginRight: "8px",
                }}
              >
                {option.symbol}
              </span>
              {option.code}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Currency"
              color="secondary"
              placeholder="Choose from the list"
            />
          )}
        />
      </Grid>
      <Grid size={4}>
        <Autocomplete
          sx={{ width: "100%", mb: 1 }}
          options={status}
          value={receipt.status}
          onChange={(_, newValue) =>
            handleFieldChange(receipt.id, "status", newValue)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Status"
              placeholder="State of collection"
              value={""}
              // sx={{ width: "300px" }}
              InputProps={{
                ...params.InputProps,
              }}
              color="secondary"
            />
          )}
          size="small"
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer components={["DatePicker"]}>
            <DatePicker
              value={receipt.date}
              format="DD/MM/YYYY"
              onChange={(newValue) =>
                handleFieldChange(receipt.id, "date", newValue)
              }
              slotProps={{
                textField: {
                  size: "small",
                  color: "secondary",
                  // required: true,
                },
              }}
              sx={{ width: "100%" }}
              label="Date"
            />
          </DemoContainer>
        </LocalizationProvider>
      </Grid>
    </Grid>
  );
};

const AddReceiptsManualInput = () => {
  const dispatch = useDispatch();

  const { allCurrencies, allCompanies, receipts } = useSelector(
    (state: any) => state.receipts,
  );
  const companyOptions = allCompanies?.map((company: any) => ({
    label: company.finance_company_name,
    shared_uid: company.shared_uid,
  }));
  const currencyOptions = allCurrencies?.map((currency: any) => ({
    code: currency.currency_code,
    symbol: currency.currency_symbol,
    shared_uid: currency.shared_uid,
  }));

  const handleAddReceipt = () => {
    const newId =
      receipts.length > 0 ? Math.max(...receipts.map((r: any) => r.id)) + 1 : 1;
    dispatch(
      setReceipts([
        ...receipts,
        {
          id: newId,
          receiptNumber: "",
          receiptAmount: "",
          company: null,
          currency: null,
          status: { label: "Not Final" },
          date: null,
        },
      ]),
    );
  };
  const handleDeleteReceipt = (id: any) => {
    if (receipts.length > 1) {
      dispatch(
        setReceipts(receipts.filter((receipt: any) => receipt.id !== id)),
      );
    }
  };
  const handleFieldChange = (id: any, field: any, value: any) => {
    dispatch(
      setReceipts(
        receipts.map((receipt: any) =>
          receipt.id === id ? { ...receipt, [field]: value } : receipt,
        ),
      ),
    );
  };
  const buildPayload = () => {
    return receipts.map((receipt: any) => ({
      receipt_id: receipt.receiptNumber,
      receipt_date: receipt.date ? receipt.date.format("YYYY-MM-DD") : null,
      receipt_amount: receipt.receiptAmount
        ? Number(receipt.receiptAmount)
        : null,
      receipt_currency_shared_uid: receipt.currency?.shared_uid ?? null,
      company_shared_uid: receipt.company?.shared_uid ?? null,
      receipt_status: receipt.status?.label ?? null,
    }));
  };

  const duplicateReceiptNumbers = useMemo(() => {
    const counts: Record<string, number> = {};

    receipts.forEach((r: any) => {
      const value = r.receiptNumber?.trim();
      if (!value) return;
      counts[value] = (counts[value] || 0) + 1;
    });

    return new Set(Object.keys(counts).filter((key) => counts[key] > 1));
  }, [receipts]);

  useEffect(() => {
    const payload = buildPayload();
    dispatch(setManualReceiptsPayload(payload));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipts, dispatch]);

  return (
    <>
      {receipts.map((receipt: any, index: any) => (
        <ReceiptForm
          key={receipt.id}
          receipt={receipt}
          receiptNumber={index + 1}
          totalReceipts={receipts.length}
          canDelete={receipts.length > 1}
          onDelete={handleDeleteReceipt}
          handleFieldChange={handleFieldChange}
          companyOptions={companyOptions}
          currencyOptions={currencyOptions}
          duplicateReceiptNumbers={duplicateReceiptNumbers}
        />
      ))}

      <Grid container spacing={2} mt={3}>
        <Grid size={12}>
          <Button
            variant="outlined"
            size="medium"
            color="secondary"
            endIcon={<AddIcon sx={{ ml: "0.5" }} />}
            onClick={handleAddReceipt}
          >
            Add another
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default AddReceiptsManualInput;
